import { useState, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import { useToast } from './Toast'
import { Dialog } from './Dialog'
import { useAuthStore } from '../stores/authStore'

/**
 * Terms of Service Modal
 * Toont Terms of Service en vraagt om acceptatie.
 * Vereist voor GDPR compliance.
 * 
 * Gebruikt accepted_tos_at veld in User model.
 */

function TermsOfServiceModal({ user, onAccept }) {
  const { t } = useTranslation()
  const toast = useToast()
  const [isOpen, setIsOpen] = useState(false)
  
  // Check if user needs to accept TOS
  const needsAcceptance = user && !user.accepted_tos_at
  
  const acceptMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/settings/accept-tos', {
        accepted: true
      })
      return response.data
    },
    onSuccess: () => {
      if (onAccept) onAccept()
      toast.success(t('terms.toastAccepted', 'Terms of Service geaccepteerd'))
    },
    onError: () => {
      // Geen toast: optimistic update staat; wijziging is lokaal al doorgevoerd, modal blijft dicht
    }
  })
  
  useEffect(() => {
    if (needsAcceptance) {
      setIsOpen(true)
    }
  }, [needsAcceptance])
  
  if (!needsAcceptance) {
    return null
  }
  
  const sections = t('terms.sections', { returnObjects: true })
  const sectionsList = Array.isArray(sections) ? sections : []

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => {}} // Cannot close without accepting
      title={t('terms.modalTitle', 'ORION SaaS – Algemene Voorwaarden - Acceptatie Vereist')}
      size="xl"
    >
      <div className="max-h-[70vh] overflow-y-auto p-4">
        <div className="prose prose-sm max-w-none">
          <h3 className="text-lg font-bold">{t('terms.header', 'ORION SaaS')}</h3>
          <h4 className="text-base font-semibold">{t('terms.subheader', 'ALGEMENE VOORWAARDEN – PRIVACYBELEID – SLA – VERWERKERSOVEREENKOMST')}</h4>
          <p className="text-sm text-gray-600 mb-4">
            <strong>{t('terms.lastUpdate', 'Laatste update:')}</strong> {t('terms.lastUpdateDate', '20 februari 2026')}
          </p>

          {sectionsList.map((section, i) => (
            <div key={i}>
              <h4 className="font-semibold mt-4">{section.title}</h4>
              <p>{section.content}</p>
            </div>
          ))}
        </div>
      </div>
      
      <div className="flex justify-end gap-2 p-4 border-t">
        <button
          onClick={() => {
            // Logout user if they don't accept (gebruik authStore voor consistente cleanup)
            useAuthStore.getState().logout()
            window.location.href = '/login'
          }}
          className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
        >
          {t('terms.reject', 'Weigeren & Uitloggen')}
        </button>
        <button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            // Optimistic update: direct store + localStorage bijwerken zodat modal verdwijnt
            if (onAccept) onAccept()
            setIsOpen(false)
            acceptMutation.mutate()
          }}
          disabled={acceptMutation.isPending}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {acceptMutation.isPending ? t('terms.accepting', 'Bezig...') : t('terms.accept', 'Accepteren & Doorgaan')}
        </button>
      </div>
    </Dialog>
  )
}

export default TermsOfServiceModal
